/* eslint-disable import/no-unresolved */
import {
  FormattedDate,
  FormattedMessage,
  injectIntl,
  intlShape,
} from '@edx/frontend-platform/i18n';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Grid, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Hyperlink } from '@edx/paragon';
import { AppContext } from '@edx/frontend-platform/react';

// i18n
import messages from './Certificates.messages';

// Components
import SwitchContent from './elements/SwitchContent';

// Assets
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import professionalCertificateSVG from '../assets/professional-certificate.svg';
import verifiedCertificateSVG from '../assets/verified-certificate.svg';
// Selectors
import { certificatesSelector } from '../data/selectors';
import CircleButton from './elements/CircleButton';

class Certificates extends React.Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.props.changeHandler(name, value);
  }

  handleSubmit(e) {
    e.preventDefault();
    this.props.submitHandler(this.props.formId);
  }

  handleClose() {
    this.props.closeHandler(this.props.formId);
  }

  handleOpen() {
    this.props.openHandler(this.props.formId);
  }

  renderCertificate({
    certificateType,
    courseDisplayName,
    courseOrganization,
    modifiedDate,
    downloadUrl,
    courseId,
    grade,
  }) {
    const { intl } = this.props;
    const certificateIllustration = (() => {
      switch (certificateType) {
        case 'professional':
        case 'no-id-professional':
          return professionalCertificateSVG;
        case 'verified':
          return verifiedCertificateSVG;
        case 'honor':
        case 'audit':
        default:
          return null;
      }
    })();
    const colors = ['tw-bg-lightBlue', 'tw-bg-lightPurple'];
    const randomColorClass = colors[Math.floor(Math.random() * colors.length)];

    return (
      <SwiperSlide key={`${modifiedDate}-${courseId}`} className="d-flex align-items-stretch">
        <div
          className={`tw-w-[200px] tw-min-h-[200px] ${randomColorClass} tw-rounded-[24px] md:tw-w-[260px] md:tw-min-h-[260px]`}
        >
          <div className="card-body d-flex flex-column tw-h-full">
            <h4 className="card-title tw-mb-4 tw-text-[20px] tw-text-neutral-1000 tw-font-medium">
              {courseDisplayName}
            </h4>
            <p className="tw-text-[12px] tw-text-neutral-700 tw-font-medium tw-mb-[50px] md:tw-text-[16px]">
              {intl.formatMessage(messages['profile.certificate.completion.date.label'], {
                date: <FormattedDate value={new Date(modifiedDate)} />,
              })}
            </p>
            <div className="tw-mt-auto tw-flex tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-mt-[3px]">
                  <svg
                    width="18"
                    height="24"
                    viewBox="0 0 18 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_7649_18723)">
                      <path
                        d="M8.9987 15.8295L14.6637 19.2487L13.1604 12.8045L18.1654 8.4687L11.5745 7.90036L8.9987 1.83203L6.42286 7.90036L-0.167969 8.4687L4.82786 12.8045L3.3337 19.2487L8.9987 15.8295Z"
                        fill="#110F12"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_7649_18723">
                        <rect width="18" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </span>
                <span className="tw-font-medium">{grade}</span>
              </div>
              <a href={downloadUrl} target="_blank">
                <span>
                  <svg
                    width="44"
                    height="43"
                    viewBox="0 0 44 43"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="44" height="43" rx="21.5" fill="white" />
                    <path d="M15 30H29V28H15M29 19H25V13H19V19H15L22 26L29 19Z" fill="#050505" />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </SwiperSlide>
    );
  }

  renderCertificates() {
    const { intl } = this.props;

    if (this.props.certificates === null || this.props.certificates.length === 0) {
      return (
        <AppContext.Consumer>
          {context => (
            <div className="tw-pr-10 md:tw-pr-12 xl:tw-pr-[80px]">
              <p className="tw-font-medium !tw-mb-8 md:!tw-mb-10">
                {intl.formatMessage(messages['profile.no.certificates'])}
              </p>
              <Hyperlink
                className="btn btn-secondary tw-w-full md:tw-w-[280px]"
                destination={`${context.config.MKTG_BASE_URL}courses-catalog/`}
              >
                {intl.formatMessage(messages['profile.to.all.courses.button'])}
              </Hyperlink>
            </div>
          )}
        </AppContext.Consumer>
      );
    }

    return (
      <Swiper
        // Right gap after the last slide. Padding (not slidesOffsetAfter) keeps Swiper's
        // overflow lock working, so the nav arrows hide when all slides fit.
        className="!tw-pr-6 md:!tw-pr-12 xl:!tw-pr-[80px]"
        modules={[Navigation]}
        spaceBetween={16}
        slidesPerView="auto"
        navigation={{
          nextEl: '.custom-next-button', // Bind to custom button
          prevEl: '.custom-prev-button', // Bind to custom button
        }}
        breakpoints={{
          768: {
            spaceBetween: 24,
          },
          1440: {
            spaceBetween: 32,
          },
        }}
      >
        {this.props.certificates.map(certificate => this.renderCertificate(certificate))}
      </Swiper>
    );
  }

  render() {
    const { visibilityCourseCertificates, editMode, saveState, intl } = this.props;

    // Handle null editMode by defaulting to 'static'
    const mode = editMode || 'static';

    const hasCertificates =
      this.props.certificates !== null && this.props.certificates.length !== 0;

    return (
      <SwitchContent
        expression={mode}
        cases={{
          editing: (
            <div role="dialog" aria-labelledby="course-certificates-label">
              <form onSubmit={this.handleSubmit}>
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-8 md:tw-mb-10 xl:tw-mb-12 xl:tw-pr-[80px]">
                  <h6 className="tw-text-2xl tw-font-medium tw-leading-[120%] md:tw-text-[28px]">
                    {intl.formatMessage(messages['profile.certificates.my.certificates'])}
                  </h6>

                  {/* Custom Swiper Navigation Buttons */}
                  {hasCertificates && (
                    <div className="tw-hidden xl:tw-flex tw-gap-4">
                      <CircleButton
                        className="custom-prev-button tw-btn-prev"
                        ariaLabel="prev slide"
                      />
                      <CircleButton
                        className="custom-next-button tw-btn-next"
                        ariaLabel="next slide"
                      />
                    </div>
                  )}
                </div>
                {this.renderCertificates()}
              </form>
            </div>
          ),
          editable: (
            <>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-8 md:tw-mb-10 xl:tw-mb-12 xl:tw-pr-[80px]">
                <h6 className="tw-text-2xl tw-font-medium tw-leading-[120%] md:tw-text-[28px]">
                  {intl.formatMessage(messages['profile.certificates.my.certificates'])}
                </h6>

                {/* Custom Swiper Navigation Buttons - Desktop only when certificates exist */}
                {hasCertificates && (
                  <div className="tw-hidden xl:tw-flex tw-gap-4">
                    <CircleButton
                      className="custom-prev-button tw-btn-prev"
                      ariaLabel="prev slide"
                    />
                    <CircleButton
                      className="custom-next-button tw-btn-next"
                      ariaLabel="next slide"
                    />
                  </div>
                )}
              </div>
              {this.renderCertificates()}
            </>
          ),
          empty: (
            <>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-8 md:tw-mb-10 xl:tw-mb-12 xl:tw-pr-[80px]">
                <h6 className="tw-text-2xl tw-font-medium tw-leading-[120%] md:tw-text-[28px]">
                  {intl.formatMessage(messages['profile.certificates.my.certificates'])}
                </h6>

                {/* Custom Swiper Navigation Buttons */}
                {hasCertificates && (
                  <div className="tw-hidden xl:tw-flex tw-gap-4">
                    <CircleButton
                      className="custom-prev-button tw-btn-prev"
                      ariaLabel="prev slide"
                    />
                    <CircleButton
                      className="custom-next-button tw-btn-next"
                      ariaLabel="next slide"
                    />
                  </div>
                )}
              </div>
              {this.renderCertificates()}
            </>
          ),
          static: (
            <>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-8 md:tw-mb-10 xl:tw-mb-12 xl:tw-pr-[80px]">
                <h6 className="tw-text-2xl tw-font-medium tw-leading-[120%] md:tw-text-[28px]">
                  {intl.formatMessage(messages['profile.certificates.my.certificates'])}
                </h6>

                {/* Custom Swiper Navigation Buttons - Desktop only when certificates exist */}
                {hasCertificates && (
                  <div className="tw-hidden xl:tw-flex tw-gap-4">
                    <CircleButton
                      className="custom-prev-button tw-btn-prev"
                      ariaLabel="prev slide"
                    />
                    <CircleButton
                      className="custom-next-button tw-btn-next"
                      ariaLabel="next slide"
                    />
                  </div>
                )}
              </div>
              {this.renderCertificates()}
            </>
          ),
        }}
      />
    );
  }
}

Certificates.propTypes = {
  // It'd be nice to just set this as a defaultProps...
  // except the class that comes out on the other side of react-redux's
  // connect() method won't have it anymore. Static properties won't survive
  // through the higher order function.
  formId: PropTypes.string.isRequired,

  // From Selector
  certificates: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
    }),
  ),
  visibilityCourseCertificates: PropTypes.oneOf(['private', 'all_users']),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,

  // Actions
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,

  // i18n
  intl: intlShape.isRequired,
};

Certificates.defaultProps = {
  editMode: 'static',
  saveState: null,
  visibilityCourseCertificates: 'private',
  certificates: null,
};

export default connect(certificatesSelector, {})(injectIntl(Certificates));
